import { useEffect, useState } from 'react';
import axios from 'axios';
import { Copy, Sparkles, ChevronRight } from 'lucide-react';
import CopyWatchlistButton from '@/components/CopyWatchlistButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TrendingWatchlistsRibbon — Round 84b (Feb 14, 2026).
 *
 * Surfaces the watchlists with the most copies in the last 30 days
 * at the top of the Community Hub. Builds a social-proof loop for
 * the new Copy mechanic — every copy event is date-stamped in
 * `community_watchlist_copies` so the ribbon reflects RECENT
 * activity, not all-time totals.
 *
 * Self-hides when no recent copies exist anywhere so the slot stays
 * invisible until the mechanic gains traction organically.
 */
export default function TrendingWatchlistsRibbon({ myHandle }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/community/watchlists/trending`, { params: { days: 30, limit: 5 } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data || !data.watchlists || data.watchlists.length === 0) return null;

  return (
    <div
      className="rounded-2xl border border-amber-300 dark:border-amber-700 bg-gradient-to-br from-amber-50/60 via-card to-card dark:from-amber-950/30 dark:via-card dark:to-card p-4 sm:p-5"
      data-testid="trending-watchlists-ribbon"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-950/40 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold flex items-center gap-2">
            <Copy className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            Trending watchlists
          </h3>
          <p className="text-[11px] text-muted-foreground">
            Most copied in the last {data.days} days · {data.total_copies} total {data.total_copies === 1 ? 'copy' : 'copies'}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {data.watchlists.map((w, idx) => (
          <div
            key={w.watchlist_id}
            className="rounded-lg border border-border bg-background p-2.5 flex items-start gap-2"
            data-testid={`trending-watchlist-row-${w.watchlist_id}`}
          >
            <div className="w-7 h-7 rounded-md bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 flex items-center justify-center text-xs font-bold flex-shrink-0">
              #{idx + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm truncate">{w.name}</span>
                <span className="text-[10px] text-muted-foreground">by @{w.handle}</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300">
                  <Copy className="w-2.5 h-2.5" />
                  {w.recent_copies} {w.recent_copies === 1 ? 'copy' : 'copies'} · {data.days}d
                </span>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {(w.tickers || []).map((t) => (
                  <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted">
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0">
              {myHandle && myHandle !== w.handle ? (
                <CopyWatchlistButton watchlistId={w.watchlist_id} />
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
